import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/chadcn/ContextMenu"
import {
  EmrNotificationApiDto,
  NotificationLogEntryStatus,
} from "@/model/Patient"
import cn from "@/utils/cn"
import { notificationTypeIndicatorColor } from "@/utils/notification-utils"
import { DateTimeFormatter, Instant, ZoneId } from "@js-joda/core"
import { Locale } from "@js-joda/locale"

type EMRNotificationsTableProps = {
  notifications: EmrNotificationApiDto[]
  clinicTimeZone: string
  highlightedId: string | null
  onNavigateToCell: (timestamp: Instant) => void
}

const getStatusBadgeClass = (status: NotificationLogEntryStatus) => {
  switch (status) {
    case "SENT":
      return "bg-green-100 text-green-700"
    case "PENDING":
    case "SENDING":
      return "bg-yellow-100 text-yellow-700"
    case "SEND_FAILED":
      return "bg-red-100 text-red-700"
    case "SKIPPED":
    case "DISABLED":
    case "INACTIVE_PRESCRIPTION":
      return "bg-gray-100 text-gray-700"
  }
}

const EMRNotificationRow = ({
  notification,
  clinicTimeZone,
  isHighlighted,
  isLast,
  onNavigateToCell
}: {
  notification: EmrNotificationApiDto
  clinicTimeZone: string
  isHighlighted: boolean
  isLast: boolean
  onNavigateToCell: (timestamp: Instant) => void
}) => {
  const formatTimestamp = (timestamp: Instant) =>
    timestamp
      .atZone(ZoneId.of(clinicTimeZone))
      .format(
        DateTimeFormatter.ofPattern("MMM dd yyyy, h:mm a").withLocale(
          Locale.US
        )
      )


  const enqueuedTimestamp = Instant.parse(notification.enqueuedAt)
  const sentTimestamp = notification.sentAt
    ? Instant.parse(notification.sentAt)
    : null

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <tr
          id={`notification-row-${notification.id}`}
          key={notification.id}
          className={cn(
            isLast ? "" : "border-b border-gray-200",
            !isHighlighted ? "bg-white" : "animate-highlightPulse"
          )}
        >
          <td
            className={cn(
              "px-4 py-3 text-sm",
              isLast && "rounded-bl-xl"
            )}
          >
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "h-4 w-1 rounded-full",
                  notificationTypeIndicatorColor(notification.type)
                )}
              />
              <span>{notification.type}</span>
            </div>
          </td>
          <td className="px-4 py-3">
            <span
              className={cn(
                "inline-block rounded-full px-3 py-1 text-xs font-medium",
                getStatusBadgeClass(notification.status)
              )}
            >
              {notification.status}
            </span>
          </td>
          <td className="px-4 py-3 text-sm">
            {formatTimestamp(enqueuedTimestamp)}
          </td>
          <td
            className={cn(
              "px-4 py-3 text-sm",
              isLast && "rounded-br-xl"
            )}
          >
            {sentTimestamp ? formatTimestamp(sentTimestamp) : "N/A"}
          </td>
        </tr>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onClick={() => onNavigateToCell(sentTimestamp ?? enqueuedTimestamp)}
        >
          Jump to calendar cell
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
const EMRNotificationsTable = ({
  notifications,
  clinicTimeZone,
  highlightedId,
  onNavigateToCell,
}: EMRNotificationsTableProps) => {
  if (notifications.length === 0) {
    return (
      <div className="py-8 text-center italic text-gray-500">
        No EMR notifications
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="rounded-xl bg-gray-100">
          <tr>
            <th className="rounded-tl-xl px-4 py-3 text-left text-sm font-semibold">
              Report Type
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Status
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Enqueued At
            </th>
            <th className="rounded-tr-xl px-4 py-3 text-left text-sm font-semibold">
              Sent At
            </th>
          </tr>
        </thead>
        <tbody>
          {notifications.map((notification, index) => (
            <EMRNotificationRow
              key={notification.id}
              notification={notification}
              clinicTimeZone={clinicTimeZone}
              isHighlighted={highlightedId === notification.id}
              isLast={index === notifications.length - 1}
              onNavigateToCell={onNavigateToCell}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default EMRNotificationsTable
